import React, { useState } from 'react';
import { PreorderResult, Region, TrackProductInput } from '../types';

interface Props {
  product: PreorderResult;
  onConfirm: (input: TrackProductInput) => void;
  onClose: () => void;
}

const ALL_REGIONS: Region[] = ['ES', 'EU', 'UK', 'US'];

export function TrackDialog({ product, onConfirm, onClose }: Props) {
  const [regions, setRegions] = useState<Region[]>([product.region]);
  const [targetPrice, setTargetPrice] = useState('');
  const [notifyByEmail, setNotifyByEmail] = useState(false);
  const [notifyByTelegram, setNotifyByTelegram] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [error, setError] = useState('');

  function toggleRegion(r: Region) {
    setRegions((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  }

  function handleConfirm() {
    setError('');
    if (regions.length === 0) { setError('Select at least one region.'); return; }
    if (notifyByEmail && !notificationEmail) { setError('Enter your email address.'); return; }
    if (notifyByTelegram && !telegramChatId) { setError('Enter your Telegram chat ID.'); return; }

    const parsed = targetPrice ? parseFloat(targetPrice) : undefined;
    if (parsed !== undefined && (isNaN(parsed) || parsed <= 0)) {
      setError('Target price must be a positive number.');
      return;
    }

    onConfirm({
      productId: product.productId,
      productTitle: product.productTitle,
      productType: product.productType,
      regions,
      targetPrice: parsed,
      notifyByEmail,
      notifyByTelegram,
      notificationEmail: notifyByEmail ? notificationEmail : undefined,
      telegramChatId: notifyByTelegram ? telegramChatId : undefined,
    });
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <h3 style={{ fontSize: '16px', fontWeight: 700 }}>Track Product</h3>
          <button style={closeBtn} onClick={onClose}>✕</button>
        </div>

        <p style={{ color: '#8a93b0', fontSize: '14px', marginBottom: '20px' }}>{product.productTitle}</p>

        <div style={group}>
          <label style={lbl}>Regions to Track</label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {ALL_REGIONS.map((r) => (
              <label key={r} style={chkLbl}>
                <input type="checkbox" checked={regions.includes(r)} onChange={() => toggleRegion(r)} style={{ accentColor: '#f97316' }} />
                {r}
              </label>
            ))}
          </div>
        </div>

        <div style={group}>
          <label style={lbl} htmlFor="targetPrice">Target Price (optional)</label>
          <input
            id="targetPrice"
            type="number"
            min="0"
            step="0.01"
            placeholder="e.g. 75.00"
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
            style={input}
          />
          <span style={{ fontSize: '12px', color: '#8a93b0' }}>Notify when price drops to or below this value</span>
        </div>

        <div style={group}>
          <label style={lbl}>Notifications</label>
          <label style={chkLbl}>
            <input type="checkbox" checked={notifyByEmail} onChange={(e) => setNotifyByEmail(e.target.checked)} style={{ accentColor: '#f97316' }} />
            Email
          </label>
          {notifyByEmail && (
            <input
              type="email"
              placeholder="your@email.com"
              value={notificationEmail}
              onChange={(e) => setNotificationEmail(e.target.value)}
              style={{ ...input, marginTop: '8px' }}
            />
          )}
          <label style={{ ...chkLbl, marginTop: '8px' }}>
            <input type="checkbox" checked={notifyByTelegram} onChange={(e) => setNotifyByTelegram(e.target.checked)} style={{ accentColor: '#f97316' }} />
            Telegram
          </label>
          {notifyByTelegram && (
            <input
              type="text"
              placeholder="Telegram chat ID (e.g. 123456789)"
              value={telegramChatId}
              onChange={(e) => setTelegramChatId(e.target.value)}
              style={{ ...input, marginTop: '8px' }}
            />
          )}
        </div>

        {error && <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button style={cancelBtn} onClick={onClose}>Cancel</button>
          <button style={confirmBtn} onClick={handleConfirm}>Save to Tracked</button>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '16px',
};
const modal: React.CSSProperties = {
  background: '#1a1d27', borderRadius: '12px', padding: '24px',
  width: '100%', maxWidth: '480px', border: '1.5px solid #2e3347',
};
const header: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' };
const closeBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#8a93b0', cursor: 'pointer', fontSize: '18px' };
const group: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' };
const lbl: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: '#8a93b0', textTransform: 'uppercase', letterSpacing: '0.05em' };
const chkLbl: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' };
const input: React.CSSProperties = {
  padding: '9px 12px', background: '#22263a', border: '1.5px solid #2e3347',
  borderRadius: '7px', color: '#e8eaf0', fontSize: '14px', outline: 'none',
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 18px', background: 'transparent', border: '1.5px solid #2e3347',
  borderRadius: '7px', color: '#8a93b0', cursor: 'pointer', fontSize: '14px',
};
const confirmBtn: React.CSSProperties = {
  padding: '9px 18px', background: '#f97316', border: 'none',
  borderRadius: '7px', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '14px',
};
